import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { agenciaStoreModule } from '@/store_pinia/agencia';
import { appGlobalStore } from '@/store_pinia/app';
import i18n from '@/i18n';

export function useStructuredData() {
  const route = useRoute()
  const agenciaStore = agenciaStoreModule();
  const storeGlobal = appGlobalStore();
  const { t } = i18n.global;

  // Configuracion base (ajusta segun tu proyecto)
  const config = {
    siteName: agenciaStore.getMetaTitulo,
    siteUrl: window.location.origin,
    logo: storeGlobal.valores.img_logo_agencia,
    organizationName: agenciaStore.getMetaTitulo,
    description: agenciaStore.getMetaDescripcion,
    language: agenciaStore.getLocale
  }

  // Schema Organization (usar solo en home o about)
  const organizationSchema = computed(() => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": config.organizationName,
    "url": config.siteUrl,
    "logo": config.logo,
    "description": config.description,
    // "address": {
    //   "@type": "PostalAddress",
    //   "streetAddress": "Tu dirección",
    //   "addressLocality": "Tu ciudad",
    //   "addressRegion": "Tu región",
    //   "postalCode": "00000",
    //   "addressCountry": "ES"
    // },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": agenciaStore.getTelefono,
      "contactType": "customer service",
      "email": agenciaStore.getEmail,
      "availableLanguage": [config.language]
    },
    // "sameAs": [
    //   "https://www.facebook.com/tuempresa",
    //   "https://www.twitter.com/tuempresa",
    //   "https://www.linkedin.com/company/tuempresa",
    //   "https://www.instagram.com/tuempresa"
    // ]
  }));

  // Schema WebSite (usar en todas las paginas)
  const websiteSchema = computed(() => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": config.siteName,
    "url": config.siteUrl,
    "description": config.description,
    "inLanguage": config.language,
    "publisher": {
      "@type": "Organization",
      "name": config.organizationName,
      "logo": {
        "@type": "ImageObject",
        "url": config.logo
      }
    },
    // "potentialAction": {
    //   "@type": "SearchAction",
    //   "target": {
    //     "@type": "EntryPoint",
    //     "urlTemplate": `${config.siteUrl}/buscar?q={search_term_string}`
    //   },
    //   "query-input": "required name=search_term_string"
    // }
  }));

  // Schema WebPage dinamico
  function createWebPageSchema(options = {}) {
    const {
      pageType = 'WebPage',
      title = route.meta?.title || config.siteName,
      description = route.meta?.description || config.description,
      breadcrumbs = null,
      image = null,
      additionalProperties = {}
    } = options

    const currentUrl = `${config.siteUrl}${route.path}`

    const schema = {
      "@context": "https://schema.org",
      "@type": pageType,
      "name": title,
      "description": description,
      "url": currentUrl,
      "inLanguage": config.language,
      "isPartOf": {
        "@type": "WebSite",
        "url": config.siteUrl,
        "name": config.siteName
      }
    }

    // Aniadir breadcrumbs si se proporcionan
    if (breadcrumbs) {
      schema.breadcrumb = {
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((item, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": item.name,
          "item": `${config.siteUrl}${item.path}`
        }))
      }
    }

    // Aniadir imagen si se proporciona
    if (image) {
      schema.primaryImageOfPage = {
        "@type": "ImageObject",
        "url": image.url || image,
        ...(image.width && { width: image.width }),
        ...(image.height && { height: image.height })
      }
    }

    // Añadir propiedades adicionales
    return { ...schema, ...additionalProperties }
  }

  // Helper para crear breadcrumbs automaticamente desde la ruta
  function createBreadcrumbsFromRoute() {
    const paths = route.path.split('/').filter(p => p)
    const breadcrumbs = [{ name: t('seo.home.title'), path: '/' }]

    let currentPath = ''
    paths.forEach((segment, index) => {
      currentPath += `/${segment}`
      // Intentar obtener el nombre de la meta del router
      const name = route.matched[index + 1]?.meta?.breadcrumbName ||
        segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ')

      breadcrumbs.push({
        name,
        path: currentPath
      })
    })

    return breadcrumbs
  }

  // Schema para FAQPage
  function createFAQSchema(faqs) {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.pregunta,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.respuesta
        }
      }))
    };
  }

  // Schema para Product
  function createProductSchema(product) {
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.name,
      "description": product.description,
      "image": product.image || [],
      "sku": product.sku,
      "brand": {
        "@type": "Brand",
        "name": product.brand || config.organizationName
      },
      "offers": {
        "@type": "Offer",
        "url": `${config.siteUrl}${route.path}`,
        "priceCurrency": product.currency || "EUR",
        "price": product.price,
        "availability": product.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        ...(product.priceValidUntil && { priceValidUntil: product.priceValidUntil })
      },
      ...(product.aggregateRating && {
        aggregateRating: {
          "@type": "AggregateRating",
          "ratingValue": product.aggregateRating.value,
          "reviewCount": product.aggregateRating.count
        }
      })
    };
  }

  return {
    config,
    organizationSchema,
    websiteSchema,
    createWebPageSchema,
    createBreadcrumbsFromRoute,
    createFAQSchema,
    createProductSchema
  }
}